import { ThemeProvider, createTheme } from "@mui/material/styles";
import { ErrorBoundary as SentryErrorBoundary } from "@sentry/react";
import * as Sentry from "@sentry/react";
import { QueryErrorResetBoundary, useIsRestoring } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { Provider as ReduxProvider } from "react-redux";
import { Toaster } from "sonner";
import PWABadge from "./PWABadge";
import ModalPrompt from "./components/modal/ModalPrompt";
import { queryClient, queryClientPersister } from "./queries";
import { store } from "./redux/store";
import { routeTree } from "./routeTree.gen";
import ErrorPage, { ErrorFallback } from "./routes/-components/ErrorPage";
import NotFound from "./routes/-components/NotFound";
import PendingComponent from "./routes/-components/PendingComponent";

const defaultPreload =
	import.meta.env.VITE_APP_URL === "https://app.hemiteams.ai"
		? "intent"
		: false;

const router = createRouter({
	routeTree,
	context: {
		queryClient,
		isRestoring: false,
	},
	defaultPreload,
	scrollRestoration: true,
	defaultStructuralSharing: true,
	defaultPreloadStaleTime: 0,
	defaultNotFoundComponent: NotFound,
	defaultPendingComponent: PendingComponent,
	defaultErrorComponent: ErrorPage,
});

declare module "@tanstack/react-router" {
	interface Register {
		router: typeof router;
	}

	interface RouteContext {
		queryClient: typeof queryClient;
		isRestoring: boolean;
	}
}

Sentry.init({
	dsn: import.meta.env.VITE_SENTRY_DSN,
	enabled: !!import.meta.env.VITE_SENTRY_DSN,
	debug: import.meta.env.DEV,
	environment: import.meta.env.VITE_APP_ENV ?? "development",
	integrations: [Sentry.tanstackRouterBrowserTracingIntegration(router)],
	tracesSampleRate: import.meta.env.VITE_APP_ENV === "production" ? 0.1 : 1.0,
	replaysSessionSampleRate:
		import.meta.env.VITE_APP_ENV === "production" ? 0.05 : 1.0,
	replaysOnErrorSampleRate: 1.0,
	sendDefaultPii: true,
	release: import.meta.env.VITE_APP_VERSION,
});

export const mainTheme = createTheme({
	typography: {
		htmlFontSize: 16,
		fontFamily: [
			"Lato",
			"Montserrat",
			"Roboto",
			"Helvetica",
			"Arial",
			"sans-serif",
		].join(","),
	},
});

const App = () => {
	const isRestoring = useIsRestoring();
	return (
		<PersistQueryClientProvider
			client={queryClient}
			persistOptions={{
				persister: queryClientPersister,
				maxAge: 1000 * 60 * 60 * 12,
				buster: "v1",
			}}
			onSuccess={() => {
				queryClient.resumePausedMutations().then(() => {
					queryClient.invalidateQueries();
				});
			}}
		>
			<QueryErrorResetBoundary>
				{({ reset }) => (
					<SentryErrorBoundary
						fallback={<ErrorFallback reset={reset} />}
						onReset={reset}
					>
						<ReduxProvider store={store}>
							<ThemeProvider theme={mainTheme}>
								<RouterProvider
									router={router}
									context={{ queryClient, isRestoring }}
								/>
							</ThemeProvider>
							<Toaster
								richColors
								className="[&_li]:!bg-dark [&_li]:!border-dark [&_li]:rounded-xl data-[title]:[&_li_div]:!text-white"
							/>
							<PWABadge />
							<ModalPrompt />
						</ReduxProvider>
					</SentryErrorBoundary>
				)}
			</QueryErrorResetBoundary>
		</PersistQueryClientProvider>
	);
};

export default Sentry.withProfiler(App, {
	disabled: !import.meta.env.VITE_SENTRY_DSN,
});
